import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackAction, trackModalOpen, trackSearch } from '../lib/utils';
import DuplicateWarningDialog from '../components/common/DuplicateWarningDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { FolderOpen, Plus, Edit, Trash2, MoreVertical, Loader2, RotateCcw, Archive, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { formatShortDate } from '../lib/utils';

const CategoriesPage = () => {
  const { api, isAdmin, hasPermission, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [deletedCategories, setDeletedCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Permission check for bulk delete
  const canBulkDelete = hasPermission('bulk_delete_categories') || isSuperAdmin();
  const [form, setForm] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('active');
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const [activeRes, deletedRes] = await Promise.all([
        api().get('/categories'),
        isAdmin ? api().get('/categories/deleted').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setCategories(activeRes.data);
      setDeletedCategories(deletedRes.data || []);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (categoryId, categoryName) => {
    try {
      await api().post(`/categories/${categoryId}/restore`);
      toast.success(`Categoría "${categoryName}" restaurada`);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  // Bulk selection functions
  const toggleSelectItem = (itemId, e) => {
    e?.stopPropagation?.();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const currentList = activeTab === 'active' ? categories : deletedCategories;
    if (selectedItems.size === currentList.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(currentList.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(`¿Eliminar ${selectedItems.size} categoría(s)?`)) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = [];
    
    try {
      for (const itemId of selectedItems) {
        try {
          await api().delete(`/categories/${itemId}`);
          deleted++;
        } catch (error) {
          const errorMsg = error.response?.data?.detail || 'Error desconocido';
          const category = categories.find(c => c.id === itemId);
          errors.push(`${category?.name || itemId}: ${errorMsg}`);
          console.error(`Error deleting category ${itemId}:`, error);
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} categoría(s) eliminada(s)`);
      }
      
      if (errors.length > 0) {
        toast.error(`No se pudieron eliminar ${errors.length} categoría(s):\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}`, {
          duration: 8000
        });
      }
      
      setSelectedItems(new Set());
      loadCategories();
    } catch (error) {
      toast.error('Error al eliminar categorías');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setForm({ name: '', description: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setForm({ name: category.name, description: category.description || '' });
    setIsModalOpen(true);
  };

  const [duplicateError, setDuplicateError] = useState(null);

  const performCreate = async (forceCreate = false) => {
    const url = forceCreate ? '/categories?force_create=true' : '/categories';
    return api().post(url, form);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await api().put(`/categories/${editingCategory.id}`, form);
        toast.success('Categoría actualizada');
      } else {
        await performCreate(false);
        toast.success('Categoría creada');
      }
      setIsModalOpen(false);
      loadCategories();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // 409 + duplicate flag → ask the user to confirm or cancel
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate) {
        setDuplicateError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = async () => {
    setDuplicateError(null);
    setSaving(true);
    try {
      await performCreate(true);
      toast.success('Categoría creada (duplicado forzado)');
      setIsModalOpen(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;
    
    try {
      await api().delete(`/categories/${id}`);
      toast.success('Categoría eliminada');
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Categorías</h1>
          <p className="text-muted-foreground mt-1">
            Administra las categorías de equipos
          </p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active">
            Activas
            <Badge variant="secondary" className="ml-2">{categories.length}</Badge>
          </TabsTrigger>
          {canBulkDelete && (
            <TabsTrigger value="deleted" className="flex items-center gap-1">
              <Archive className="h-4 w-4" />
              Eliminadas
              {deletedCategories.length > 0 && (
                <Badge variant="destructive" className="ml-1">{deletedCategories.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active">
          {/* Bulk Selection Bar */}
          {canBulkDelete && categories.length > 0 && (
            <div className="flex items-center gap-4 p-3 mb-4 bg-muted/50 rounded-lg border">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              >
                {selectedItems.size === categories.length && categories.length > 0 ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                {selectedItems.size === categories.length ? 'Deseleccionar' : 'Seleccionar todos'}
              </button>
              
              {selectedItems.size > 0 && (
                <>
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.size} seleccionada(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Eliminar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems(new Set())}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}
          
          <Card className="card-hover">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    {canBulkDelete && <TableHead className="w-12"></TableHead>}
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Fecha de Creación</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={canBulkDelete ? 5 : 4}>
                          <Skeleton className="h-12 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canBulkDelete ? 5 : 4} className="text-center py-12">
                        <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">No hay categorías</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((cat) => (
                      <TableRow key={cat.id} className="table-row" data-testid={`category-row-${cat.id}`}>
                        {canBulkDelete && (
                          <TableCell>
                            <button
                              onClick={(e) => toggleSelectItem(cat.id, e)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                            >
                              {selectedItems.has(cat.id) ? (
                                <CheckSquare className="h-5 w-5 text-primary" />
                              ) : (
                                <Square className="h-5 w-5 text-muted-foreground" />
                              )}
                            </button>
                          </TableCell>
                        )}
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell className="text-muted-foreground">{cat.description || '-'}</TableCell>
                        <TableCell>{formatShortDate(cat.created_at)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(cat)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(cat.id)} className="text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {canBulkDelete && (
          <TabsContent value="deleted">
            <Card className="card-hover border-red-500/20">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Nombre</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Eliminada</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedCategories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-12">
                          <Archive className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">No hay categorías eliminadas</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      deletedCategories.map((cat) => (
                        <TableRow key={cat.id} className="table-row bg-red-500/5">
                          <TableCell className="font-medium">{cat.name}</TableCell>
                          <TableCell className="text-muted-foreground">{cat.description || '-'}</TableCell>
                          <TableCell>{formatShortDate(cat.deleted_at)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleRestore(cat.id, cat.name)}
                                className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restaurar
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar "${cat.name}" PERMANENTEMENTE?\n\nEsta acción NO se puede deshacer.`)) return;
                                  try {
                                    await api().delete(`/categories/${cat.id}/permanent`);
                                    toast.success(`"${cat.name}" eliminado permanentemente`);
                                    loadCategories();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Error al eliminar permanentemente');
                                  }
                                }}
                                title="Eliminar permanentemente"
                                data-testid={`category-permanent-delete-${cat.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Modifica los datos de la categoría' : 'Crea una nueva categoría de equipos'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre de la categoría"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descripción de la categoría..."
                rows={3}
                data-testid="category-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCategory ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateWarningDialog
        duplicateError={duplicateError}
        onCancel={() => setDuplicateError(null)}
        onForceCreate={handleForceCreate}
        entityLabel="categoría"
      />
    </div>
  );
};

export default CategoriesPage;
